import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "./Icon";
import { useThemeStore } from "../store/theme";
import { useAuthStore } from "../store/auth";
import { DEMO_MODE, PRACTICE_NAME, PRACTICE_LOCATION, IDLE_WARN_MINUTES } from "../lib/config";
import { initials } from "../lib/format";

interface Props {
  idleSecondsRemaining: number;
}

export function AppBar({ idleSecondsRemaining }: Props) {
  const theme = useThemeStore((s) => s.theme);
  const toggle = useThemeStore((s) => s.toggle);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const userName = user?.name ?? (DEMO_MODE ? "Demo User" : "—");
  const role = user?.role ?? (DEMO_MODE ? "Treatment Coordinator" : "");

  const mm = String(Math.floor(idleSecondsRemaining / 60)).padStart(2, "0");
  const ss = String(idleSecondsRemaining % 60).padStart(2, "0");
  const warn = idleSecondsRemaining <= IDLE_WARN_MINUTES * 60;

  const handleSignOut = async () => {
    if (DEMO_MODE) return;
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="appbar">
      <div className="appbar__brand">
        <span className="appbar__mark">
          <Icon.Heart width={22} height={22} />
        </span>
        <div>
          <div className="appbar__title">Financing Copilot</div>
          <div className="appbar__sub">{PRACTICE_NAME} — {PRACTICE_LOCATION}</div>
        </div>
      </div>

      <nav className="appbar__nav">
        <NavLink to="/sessions" className={({ isActive }) => (isActive ? "is-active" : "")}>Sessions</NavLink>
        <NavLink to="/patients" className={({ isActive }) => (isActive ? "is-active" : "")}>Patients</NavLink>
        <NavLink to="/reports" className={({ isActive }) => (isActive ? "is-active" : "")}>Reports</NavLink>
        <NavLink to="/audit" className={({ isActive }) => (isActive ? "is-active" : "")}>Audit log</NavLink>
        <NavLink to="/settings" className={({ isActive }) => (isActive ? "is-active" : "")}>Settings</NavLink>
      </nav>

      <div className="appbar__right">
        <button
          type="button"
          className="appbar__themetoggle"
          onClick={toggle}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Switch to light" : "Switch to dark"}
        >
          {theme === "dark" ? <Icon.Sun width={14} height={14} /> : <Icon.Moon width={14} height={14} />}
        </button>

        <div className={`appbar__idle ${warn ? "is-warn" : ""}`}>
          <Icon.Lock width={12} height={12} />
          <span>Auto-lock in {mm}:{ss}</span>
        </div>

        <button
          type="button"
          className="appbar__user"
          onClick={handleSignOut}
          title={DEMO_MODE ? "Demo mode — auth disabled" : "Sign out"}
          style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
        >
          <span className="appbar__avatar">{initials(userName)}</span>
          <div>
            <div className="appbar__user-name">{userName}</div>
            <div className="appbar__user-role">{role}</div>
          </div>
        </button>
      </div>
    </div>
  );
}
